import * as stylex from '@stylexjs/stylex'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'

import { breakpoints, colors, fonts, fontSizes, radii, sizes, spacing } from '../tokens/tokens.stylex'
import { Heading } from '../typography'

const styles = stylex.create({
  pageSurface: {
    backgroundColor: colors.background,
    color: colors.foreground,
    fontFamily: fonts.sans,
    minHeight: '100dvh',
    lineHeight: 1.5,
  },
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.space24,
    width: '100%',
    maxWidth: sizes.page,
    marginInline: 'auto',
    padding: { default: spacing.space32, [breakpoints.compact]: spacing.space16 },
    boxSizing: 'border-box',
  },
  workspace: { maxWidth: 'none', padding: 0, gap: spacing.space20 },
  card: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.border,
    borderRadius: radii.medium,
    backgroundColor: colors.surface,
    padding: spacing.space24,
  },
  compact: { padding: spacing.space16 },
  header: { display: 'flex', flexDirection: 'column', gap: spacing.space12 },
  workspaceHeader: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.space16,
  },
  headingGroup: { display: 'contents' },
  workspaceHeadingGroup: { display: 'grid', gap: spacing.space6, minWidth: 0, flex: `1 1 ${sizes.headerGroup}` },
  eyebrow: { color: colors.muted, fontFamily: fonts.interface, fontSize: fontSizes.small, margin: 0 },
  description: {
    margin: 0,
    maxWidth: sizes.headerText,
    color: colors.subtle,
    fontFamily: fonts.sans,
    fontSize: fontSizes.body,
    lineHeight: 1.6,
  },
  workspaceDescription: { fontSize: fontSizes.small },
  pageActions: { display: 'contents' },
  workspaceActions: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: spacing.space8 },
})

export type PageSurfaceProps = Omit<ComponentPropsWithoutRef<'div'>, 'className' | 'style' | 'color'>

/**
 * Use PageSurface to establish a product page’s background, typography, and minimum viewport height.
 * Choose Surface for a region that should not establish page geometry.
 * Applications supply content and keep navigation and data ownership in the surrounding shell.
 *
 * Status: supported.
 */
export function PageSurface(props: PageSurfaceProps) {
  return <div {...props} {...stylex.props(styles.pageSurface)} />
}

export type PageLayoutProps = {
  children: ReactNode
  variant?: 'page' | 'workspace'
}

/**
 * Use PageLayout to align a product page’s width, padding, and vertical rhythm.
 * Choose its workspace variant inside an existing shell, or Section and Container for public page bands.
 * Applications supply content and decide which shell owns page geometry.
 *
 * Status: supported.
 */
export function PageLayout({ children, variant = 'page' }: PageLayoutProps) {
  return <div {...stylex.props(styles.page, variant === 'workspace' && styles.workspace)}>{children}</div>
}

type CardOptions = {
  padding?: 'default' | 'compact'
}

export type CardProps = Omit<ComponentPropsWithoutRef<'div'>, 'className' | 'style' | 'color'> & CardOptions

/**
 * Use Card to group related product content with a border and padding.
 * Choose Surface when only the background should change, or EditorialCardLink for linked reading content.
 * Applications supply the content and own any actions or data it presents.
 *
 * Status: supported.
 */
export function Card({ padding = 'default', ...props }: CardProps) {
  return <div {...props} {...stylex.props(styles.card, padding === 'compact' && styles.compact)} />
}

export type PageHeaderProps = {
  eyebrow?: string | undefined
  title: string
  description?: string | undefined
  actions?: ReactNode
  variant?: 'page' | 'workspace' | undefined
}

/**
 * Use PageHeader for a product page title, optional description, and nearby actions.
 * Choose Heading for a section title or EditorialPage for a public reading-page header.
 * Applications supply the title and actions, and own their permissions and results.
 *
 * Status: supported.
 */
export function PageHeader({ eyebrow, title, description, actions, variant = 'page' }: PageHeaderProps) {
  return (
    <header {...stylex.props(styles.header, variant === 'workspace' && styles.workspaceHeader)}>
      <div {...stylex.props(styles.headingGroup, variant === 'workspace' && styles.workspaceHeadingGroup)}>
        {eyebrow && <p {...stylex.props(styles.eyebrow)}>{eyebrow}</p>}
        <Heading level={1} variant={variant === 'workspace' ? 'workspace' : 'display'}>
          {title}
        </Heading>
        {description && (
          <p {...stylex.props(styles.description, variant === 'workspace' && styles.workspaceDescription)}>
            {description}
          </p>
        )}
      </div>
      {actions && (
        <div {...stylex.props(variant === 'workspace' ? styles.workspaceActions : styles.pageActions)}>{actions}</div>
      )}
    </header>
  )
}
